  // src/Context/WishlistContext.jsx - YEH WAISA HI RAHEGA, KOI CHANGE NAHI
  import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
  import {
    getWishlistAPI,
    addToWishlistAPI,
    removeFromWishlistAPI,
    clearWishlistAPI
  } from '../pages/api/index.jsx';

  const WishlistContext = createContext();

  export const useWishlist = () => {
    const context = useContext(WishlistContext);
    if (!context) throw new Error('useWishlist must be used within a WishlistProvider');
    return context;
  };

  export const WishlistProvider = ({ children }) => {
    const [wishlistItems, setWishlistItems] = useState([]);
    const [wishlistCount, setWishlistCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [apiAvailable, setApiAvailable] = useState(true);
    const [error, setError] = useState(null);
    const [initialized, setInitialized] = useState(false);

    const DEFAULT_IMAGE = "/images/dailyroutine1.webp";
    const DEFAULT_IMAGE_2 = "/images/image_not_found 1.svg";
    const API_BASE_URL = "https://volumaire.backendapihub.com";

    // ========== Auth check ==========
    const isAuthenticated = useCallback(() => {
      const token = localStorage.getItem("token");
      const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";
      return token && isLoggedIn;
    }, []);

    // ========== Image helpers ==========
    const normalizeImageUrl = useCallback((url) => {
      if (!url || typeof url !== 'string') return null;
      const strUrl = url.trim();
      if (!strUrl || strUrl === "null" || strUrl === "undefined" || 
          strUrl.includes('undefined') || strUrl.includes('null')) return null;
      if (strUrl.startsWith('http://') || strUrl.startsWith('https://') || strUrl.startsWith('data:')) return strUrl;
      if (strUrl.startsWith('/')) return `${API_BASE_URL}${strUrl}`;
      return `${API_BASE_URL}/${strUrl}`;
    }, []);

    const getImageSource = useCallback((apiImage, fallback = DEFAULT_IMAGE) => {
      try {
        const normalized = normalizeImageUrl(apiImage);
        return normalized || fallback;
      } catch (error) {
        console.error("Error in getImageSource:", error);
        return fallback;
      }
    }, [normalizeImageUrl]);

    // ========== Format wishlist item ==========
    const formatWishlistItem = useCallback((item) => {
      try {
        const productId = item.productId || item.id || Date.now().toString();
        
        // Extract images
        let mainImage = DEFAULT_IMAGE;
        let hoverImage = DEFAULT_IMAGE_2;
        
        if (item.image) mainImage = normalizeImageUrl(item.image) || mainImage;
        else if (item.img1) mainImage = normalizeImageUrl(item.img1) || mainImage;
        else if (item.images && item.images.length > 0) {
          mainImage = normalizeImageUrl(item.images[0]) || mainImage;
        }
        
        if (item.img2) hoverImage = normalizeImageUrl(item.img2) || hoverImage;
        else if (item.image2) hoverImage = normalizeImageUrl(item.image2) || hoverImage;
        else if (item.images && item.images.length > 1) {
          hoverImage = normalizeImageUrl(item.images[1]) || hoverImage;
        } else {
          hoverImage = mainImage;
        }

        return {
          id: item.id || item._id || `local_${Date.now()}_${productId}`,
          wishlistId: item.wishlistId || item.id || item._id || `local_${Date.now()}_${productId}`,
          productId: productId,
          title: item.title || item.name || item.productName || 'Unnamed Product',
          name: item.title || item.name || item.productName || 'Unnamed Product',
          price: item.price || item.priceValue || item.sellingPrice || 0,
          priceValue: item.price || item.priceValue || item.sellingPrice || 0,
          image: mainImage,
          img1: mainImage,
          img2: hoverImage,
          brand: item.brand || item.brandName || "",
          inStock: item.inStock !== false && item.isActive !== false,
          createdAt: item.createdAt || new Date().toISOString()
        };
      } catch (error) {
        console.error('Error formatting wishlist item:', error, item);
        return {
          id: item.id || item._id || `local_${Date.now()}`,
          wishlistId: item.wishlistId || item.id || item._id || `local_${Date.now()}`,
          productId: item.productId || item.id || Date.now().toString(),
          title: 'Unnamed Product',
          name: 'Unnamed Product',
          price: 0,
          priceValue: 0,
          image: DEFAULT_IMAGE,
          img1: DEFAULT_IMAGE,
          img2: DEFAULT_IMAGE_2,
          brand: "",
          inStock: true,
          createdAt: new Date().toISOString()
        };
      }
    }, [normalizeImageUrl]);

    // ========== Load wishlist from API ==========
    const loadWishlist = useCallback(async () => {
      try {
        setLoading(true);
        
        // First try to load from localStorage as temporary display
        let localItems = [];
        const savedWishlist = localStorage.getItem('wishlistItems');
        if (savedWishlist) {
          try {
            localItems = JSON.parse(savedWishlist);
            const formatted = localItems.map(item => formatWishlistItem(item));
            setWishlistItems(formatted);
            setWishlistCount(formatted.length);
          } catch (err) {
            console.error('Error parsing localStorage wishlist:', err);
          }
        }

        // If user is authenticated, try to sync with API
        if (isAuthenticated()) {
          try {
            const response = await getWishlistAPI({ skipCache: true });
            
            if (response && response.data) {
              setApiAvailable(!response.fromLocalStorage);
              
              if (response.fromLocalStorage) {
                setError('Using local wishlist - API unavailable');
              } else {
                setError(null);
              }
              
              if (response.data && Array.isArray(response.data) && response.data.length > 0) {
                const formatted = response.data.map(item => formatWishlistItem(item));
                setWishlistItems(formatted);
                setWishlistCount(formatted.length);
                localStorage.setItem('wishlistItems', JSON.stringify(formatted));
              }
            }
          } catch (apiError) {
            console.log('API not available:', apiError);
            setApiAvailable(false);
            setError('Using local wishlist - API unavailable');
          }
        } else {
          setApiAvailable(false);
          setError(null);
        }
        
      } catch (error) {
        console.error('Error loading wishlist:', error);
        setError(error.message);
        setApiAvailable(false);
      } finally {
        setLoading(false);
        setInitialized(true);
      }
    }, [formatWishlistItem, isAuthenticated]);

    // ========== Initial load ==========
    useEffect(() => {
      loadWishlist();
    }, [loadWishlist]);

    // ========== Save to localStorage whenever wishlist changes ==========
    useEffect(() => {
      if (initialized) {
        localStorage.setItem('wishlistItems', JSON.stringify(wishlistItems));
        setWishlistCount(wishlistItems.length);
      }
    }, [wishlistItems, initialized]);

    // ========== ADD to wishlist (with toggle) ==========
    const addToWishlist = async (product) => {
      try {
        setLoading(true);
        
        const productId = product.id || product._id || product.productId;
        if (!productId) throw new Error('Product ID is required');

        // Check if product already exists
        const existingItem = wishlistItems.find(item => 
          String(item.productId) === String(productId)
        );

        // If exists, remove it
        if (existingItem) {
          if (isAuthenticated()) {
            try {
              await removeFromWishlistAPI(existingItem.id);
            } catch (apiError) {
              console.log('API remove failed:', apiError);
            }
          }
          
          const updatedItems = wishlistItems.filter(item => 
            String(item.productId) !== String(productId)
          );
          setWishlistItems(updatedItems);
          localStorage.setItem('wishlistItems', JSON.stringify(updatedItems));
          setError(null);
          return false;
        }

        // Create new wishlist item
        const mainImage = getImageSource(
          product.image || product.img || product.img1 || product.images?.[0], 
          DEFAULT_IMAGE
        );
        
        const hoverImage = getImageSource(
          product.img2 || product.image2 || product.images?.[1] || product.image || product.img || product.img1, 
          DEFAULT_IMAGE_2
        );

        const newItem = {
          id: `local_${Date.now()}_${productId}`,
          wishlistId: `local_${Date.now()}_${productId}`,
          productId: productId,
          title: product.title || product.name || product.productName || 'Unnamed Product',
          name: product.title || product.name || product.productName || 'Unnamed Product',
          price: product.price || product.priceValue || product.sellingPrice || 0,
          priceValue: product.price || product.priceValue || product.sellingPrice || 0,
          image: mainImage,
          img1: mainImage,
          img2: hoverImage,
          brand: product.brand || product.brandName || "",
          inStock: product.inStock !== false && product.isActive !== false,
          createdAt: new Date().toISOString()
        };

        // Try API if authenticated
        if (isAuthenticated()) {
          try {
            const apiResponse = await addToWishlistAPI(productId);
            if (apiResponse && apiResponse.data && apiResponse.data._id) {
              newItem.id = apiResponse.data._id;
              newItem.wishlistId = apiResponse.data._id;
            }
          } catch (apiError) {
            console.log('API add failed:', apiError);
          }
        }

        const updatedItems = [...wishlistItems, newItem];
        setWishlistItems(updatedItems);
        localStorage.setItem('wishlistItems', JSON.stringify(updatedItems));
        setError(null);
        return true;
        
      } catch (error) {
        console.error('Error in addToWishlist:', error);
        setError(error.message);
        return false;
      } finally {
        setLoading(false);
      }
    };

    // ========== REMOVE from wishlist ==========
    const removeFromWishlist = async (wishlistItemId) => {
      try {
        setLoading(true);
        
        if (isAuthenticated()) {
          try {
            await removeFromWishlistAPI(wishlistItemId);
          } catch (apiError) {
            console.log('API remove failed:', apiError);
          }
        }
        
        const updatedItems = wishlistItems.filter(item => 
          String(item.id) !== String(wishlistItemId) && 
          String(item.wishlistId) !== String(wishlistItemId) &&
          String(item.productId) !== String(wishlistItemId)
        );
        
        setWishlistItems(updatedItems);
        localStorage.setItem('wishlistItems', JSON.stringify(updatedItems));
        setError(null);
        return { success: true };
        
      } catch (error) {
        console.error('Error removing from wishlist:', error);
        setError(error.message);
        return { success: false, message: error.message };
      } finally {
        setLoading(false);
      }
    };

    // ========== CLEAR wishlist ==========
    const clearWishlist = async () => {
      try {
        setLoading(true);
        
        if (isAuthenticated()) {
          try {
            await clearWishlistAPI();
          } catch (apiError) {
            console.log('API clear failed:', apiError);
          }
        }
        
        setWishlistItems([]);
        localStorage.setItem('wishlistItems', JSON.stringify([]));
        setError(null);
        return { success: true };
      } catch (error) {
        console.error('Error clearing wishlist:', error);
        setError(error.message);
        return { success: false, message: error.message };
      } finally {
        setLoading(false);
      }
    };

    // ========== Helper functions ==========
    const isInWishlist = (productId) => {
      return wishlistItems.some(item => 
        String(item.productId) === String(productId)
      );
    };

    const getWishlistItem = (productId) => {
      return wishlistItems.find(item => 
        String(item.productId) === String(productId)
      );
    };

    const moveToCart = async (productId, addToCartFunction) => {
      const item = wishlistItems.find(item => 
        String(item.productId) === String(productId)
      );
      
      if (item && addToCartFunction) {
        const cartItem = {
          id: item.productId,
          title: item.title || item.name,
          price: item.price || item.priceValue,
          image: getImageSource(item.image || item.img1, DEFAULT_IMAGE),
          brand: item.brand,
          selectedSize: "default",
          selectedFragrance: "default",
          quantity: 1
        };
        
        await addToCartFunction(cartItem, 1);
        await removeFromWishlist(item.id);
        return true;
      }
      return false;
    };

    const getAllWishlistItems = () => wishlistItems;
    
    const refreshWishlist = async () => {
      await loadWishlist();
      return wishlistItems;
    };

    const showApiWarning = isAuthenticated() && !apiAvailable;

    return (
      <WishlistContext.Provider
        value={{
          wishlistItems,
          wishlistCount,
          addToWishlist,
          removeFromWishlist,
          clearWishlist,
          isInWishlist,
          getWishlistItem,
          moveToCart,
          getAllWishlistItems,
          refreshWishlist,
          loading,
          apiAvailable,
          showApiWarning,
          error,
          getImageSource
        }}
      >
        {children}
      </WishlistContext.Provider>
    );
  };